/**
 * SegmentedControl
 * 
 * Wraps MUI ToggleButtonGroup with standardized API.
 * COMPONENT_INVENTORY.md: "the one genuine addition in this pack — mint it centrally"
 * 
 * All PRs (N4, N8, N10, N12) import this component — never use ToggleButtonGroup directly.
 * 
 * Props:
 *   options: [{value, label, disabled?, tooltip?}]
 *   value: string (currently selected value, or null for nothing chosen)
 *   onChange: (value) => void
 *   size: 'small' | 'medium' (default: 'medium')
 */

import React from 'react';
import {
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

export default function SegmentedControl({
  options = [],
  value,
  onChange,
  size = 'medium',
  exclusive = true,
  fullWidth = false,
  sx = {},
  ...rest
}) {
  const theme = useTheme();

  const handleChange = (_event, newValue) => {
    // For exclusive mode, don't allow deselecting (unless value is already null)
    if (exclusive && newValue !== null) {
      onChange(newValue);
    } else if (!exclusive) {
      onChange(newValue);
    }
  };

  return (
    <ToggleButtonGroup
      value={value}
      exclusive={exclusive}
      onChange={handleChange}
      size={size}
      fullWidth={fullWidth}
      sx={{
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: size === 'small' ? theme.typography.body2.fontSize : theme.typography.body1.fontSize,
          px: size === 'small' ? 2 : 3,
          py: size === 'small' ? 0.5 : 1,
          borderColor: theme.palette.divider,
          color: theme.palette.text.secondary,
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          },
          '&.Mui-disabled': {
            color: theme.palette.action.disabled,
            borderColor: theme.palette.action.disabledBackground,
          },
        },
        ...sx,
      }}
      {...rest}
    >
      {options.map((option) => {
        const button = (
          <ToggleButton
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </ToggleButton>
        );

        // Wrap disabled buttons in Tooltip to explain why
        if (option.disabled && option.tooltip) {
          return (
            <Tooltip key={option.value} title={option.tooltip} arrow>
              <span>{button}</span>
            </Tooltip>
          );
        }

        return button;
      })}
    </ToggleButtonGroup>
  );
}
